import type { ReactNode } from "react";
import React, { useMemo } from "react";

import type { TableInstance, Row } from "react-table";
import { Table, TablePagination } from "@material-ui/core";
import type { IResponseWithLoading } from "App/Modules/Models";

import { DefaultTableBody } from "./DefaultTableBody";
import { DefaultTableHeader } from "./DefaultTableHeader";
import { TableLoadingIndicator } from "./TableLoadingIndicator";
import { TableLoadingSkeleton } from "./TableLoadingSkeleton";
import { useTableStyles } from "./TableStyles";

type DefaultTableProps<T extends object> = {
    instance: TableInstance<T>;
    loadingState?: IResponseWithLoading<any>;
    disableHeader?: boolean;
    expandDetailsTemplate?: (row: Row<T>) => ReactNode;
    rowClassName?: (row: Row<T>) => string;
    rowsPerPage?: (number | { value: number; label: string })[];
    caption?: string;
};

/** Convenience class to render a default table with loading indicator, sortable/pagable depending on table instance */
export const DefaultTable = <T extends object>({
    instance,
    loadingState,
    disableHeader,
    rowClassName,
    expandDetailsTemplate,
    rowsPerPage,
    caption,
}: DefaultTableProps<T>) => {
    const classes = useTableStyles();

    const rootClass = useMemo(
        () => (instance.plugins.some((p) => p.pluginName.includes("FlexLayout")) ? classes.flexRoot : ""),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [instance],
    );

    const captionElement = instance.plugins.some((p) => p.pluginName.includes("FlexLayout")) ? (
        <div className="caption">{caption}</div>
    ) : (
        <caption>{caption}</caption>
    );

    return (
        <>
            <Table component="div" {...instance.getTableProps()} className={rootClass}>
                {!disableHeader && (
                    <>
                        <DefaultTableHeader headerGroups={instance.headerGroups} />
                        <TableLoadingIndicator instance={instance} {...loadingState} keepMounted />
                    </>
                )}
                {(loadingState?.isLoading || loadingState?.isPartiallyLoading) && !instance.data.length ? (
                    <TableLoadingSkeleton instance={instance} data={loadingState} />
                ) : (
                    <DefaultTableBody
                        instance={instance}
                        loadingState={loadingState}
                        rowClassName={rowClassName}
                        expandDetailsTemplate={expandDetailsTemplate}
                    />
                )}
                {caption ? captionElement : null}
            </Table>
            {instance.pageOptions && instance.initialState?.pageSize < instance.rows.length && (
                <>
                    <TablePagination
                        count={instance.rows.length}
                        page={instance.state.pageIndex}
                        component="div"
                        onPageChange={(e, p) => instance.gotoPage(p)}
                        rowsPerPage={instance.state.pageSize}
                        onRowsPerPageChange={(e) => instance.setPageSize(Number(e.target.value))}
                        rowsPerPageOptions={rowsPerPage || [25, 50, 100, { label: "All", value: -1 }]}
                    />
                </>
            )}
        </>
    );
};
